import React, { useState, useEffect, useCallback } from 'react';
import apiService from '../../services/ApiService';
import { apiRoutes } from '../../shared/apiRoutes';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  Bars3Icon,
} from '@heroicons/react/24/solid';

export type SodaFieldKey = 'lk' | 'amount' | 'rate' | 'party';

interface SodaField {
  key: SodaFieldKey;
  label: string;
  description: string;
}

const ALL_FIELDS: SodaField[] = [
  { key: 'lk', label: 'LK (Team / K or L)', description: 'Team selection and Lagaya/Khadha' },
  { key: 'amount', label: 'Amount', description: 'Soda amount value' },
  { key: 'rate', label: 'Rate', description: 'Rate for the soda' },
  { key: 'party', label: 'Party Code', description: 'Party selection' },
];

const DEFAULT_SEQUENCE: SodaFieldKey[] = ['lk', 'amount', 'rate', 'party'];

const SodaFormSequence: React.FC = () => {
  const [sequence, setSequence] = useState<SodaFieldKey[]>(DEFAULT_SEQUENCE);
  const [savedSequence, setSavedSequence] = useState<SodaFieldKey[]>(DEFAULT_SEQUENCE);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  const [fetchError, setFetchError] = useState<string>('');

  const fetchSequence = useCallback(async () => {
    setLoading(true);
    setFetchError('');
    try {
      const data = await apiService.get<{ sequence: string[] }>(apiRoutes.settings.sodaFormSequence);
      if (data?.sequence && Array.isArray(data.sequence) && data.sequence.length === 4) {
        const validKeys = new Set<SodaFieldKey>(DEFAULT_SEQUENCE);
        const fetched = data.sequence.filter((k: string) => validKeys.has(k as SodaFieldKey)) as SodaFieldKey[];
        if (fetched.length === 4) {
          setSequence(fetched);
          setSavedSequence(fetched);
        }
      }
    } catch (err: any) {
      const msg = err?.error || err?.message || 'Failed to load settings';
      setFetchError(msg);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSequence();
  }, [fetchSequence]);

  useEffect(() => {
    setHasChanges(JSON.stringify(sequence) !== JSON.stringify(savedSequence));
  }, [sequence, savedSequence]);

  const moveUp = (index: number) => {
    if (index <= 0) return;
    setSequence(prev => {
      const next = [...prev];
      [next[index - 1], next[index]] = [next[index], next[index - 1]];
      return next;
    });
  };

  const moveDown = (index: number) => {
    if (index >= sequence.length - 1) return;
    setSequence(prev => {
      const next = [...prev];
      [next[index], next[index + 1]] = [next[index + 1], next[index]];
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiService.put(apiRoutes.settings.sodaFormSequence, { sequence });
      setSavedSequence([...sequence]);
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: 'Soda form sequence saved successfully', type: 'success' }
      }));
      window.dispatchEvent(new CustomEvent('soda-form-sequence-changed', { detail: { sequence } }));
    } catch (err: any) {
      const msg = err?.error || err?.message || 'Failed to save sequence';
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: msg, type: 'error' }
      }));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSequence(DEFAULT_SEQUENCE);
  };

  const getFieldInfo = (key: SodaFieldKey): SodaField =>
    ALL_FIELDS.find(f => f.key === key) || { key, label: key, description: '' };

  if (loading) {
    return (
      <div className="theme-page flex w-full justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-300 border-t-transparent" />
        <span className="ml-3 text-gray-600 text-sm">Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="theme-page w-full">
    <div className="max-w-xl mx-auto p-4 md:p-6">
      <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-5 py-4">
          <h1 className="text-lg font-bold text-white">Soda Form Field Sequence</h1>

        </div>

        <div className="p-5">
          {fetchError && (
            <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
              {fetchError}
            </div>
          )}
          <div className="space-y-2">
            {sequence.map((key, index) => {
              const field = getFieldInfo(key);
              return (
                <div
                  key={key}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg border transition-all duration-150 ${
                    hasChanges && savedSequence[index] !== key
                      ? 'border-blue-400 bg-blue-50'
                      : 'border-gray-200 bg-gray-50'
                  }`}
                >
                  <Bars3Icon className="h-5 w-5 text-gray-400 shrink-0" />
                  <span className="w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center shrink-0">
                    {index + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-gray-900 text-sm">{field.label}</div>
                    <div className="text-xs text-gray-500">{field.description}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={() => moveUp(index)}
                      disabled={index === 0}
                      className="p-1.5 rounded-md hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title="Move up"
                    >
                      <ArrowUpIcon className="h-4 w-4 text-gray-600" />
                    </button>
                    <button
                      onClick={() => moveDown(index)}
                      disabled={index === sequence.length - 1}
                      className="p-1.5 rounded-md hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      title="Move down"
                    >
                      <ArrowDownIcon className="h-4 w-4 text-gray-600" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Preview */}
          <div className="mt-6 p-4 bg-gray-800 rounded-lg">
            <div className="text-xs text-gray-400 mb-2 font-medium uppercase tracking-wide">Preview — Tab / Enter order</div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-white text-xs font-semibold">SODA:</span>
              {sequence.map((key, i) => {
                const field = getFieldInfo(key);
                return (
                  <React.Fragment key={key}>
                    <div className="px-3 py-1 bg-white rounded text-xs font-medium text-gray-700 border border-gray-300">
                      {field.label}
                    </div>
                    {i < sequence.length - 1 && (
                      <span className="text-gray-500 text-xs">→</span>
                    )}
                  </React.Fragment>
                );
              })}
              <div className="px-3 py-1 bg-blue-500 rounded text-xs font-medium text-white">
                Save
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-200">
            <button
              onClick={handleReset}
              disabled={saving || JSON.stringify(sequence) === JSON.stringify(DEFAULT_SEQUENCE)}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Reset to Default
            </button>
            <button
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? 'Saving...' : 'Save Sequence'}
            </button>
          </div>
        </div>
      </div>
    </div>
    </div>
  );
};

export default SodaFormSequence;
